import type { McpImportItem, McpImportResult } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Check, CircleAlert, FileJson, Lock } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';

const EXAMPLE = `{
  "mcpServers": {
    "filesystem": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-filesystem", "/tmp"]
    },
    "github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": { "GITHUB_PERSONAL_ACCESS_TOKEN": "\${GITHUB_TOKEN}" }
    },
    "deepwiki": { "type": "http", "url": "https://mcp.deepwiki.com/mcp" }
  }
}`;

function VariableBadge({ v }: { v: McpImportItem['variables'][number] }) {
  if (v.action === 'store')
    return (
      <Badge
        variant="secondary"
        className="gap-1 font-mono font-normal"
        title={`Used by ${v.usedBy}`}
      >
        <Lock aria-hidden /> {v.key} · saved as secret
      </Badge>
    );
  if (v.action === 'existing')
    return (
      <Badge
        variant="secondary"
        className="gap-1 font-mono font-normal"
        title={`Used by ${v.usedBy}`}
      >
        <Check aria-hidden /> {v.key}
      </Badge>
    );
  return (
    <Badge variant="outline" className="gap-1 font-mono font-normal" title={`Used by ${v.usedBy}`}>
      <CircleAlert aria-hidden /> {v.key} · not set
    </Badge>
  );
}

function Plan({ result }: { result: McpImportResult }) {
  return (
    <div className="grid gap-2" aria-label="Import preview">
      {result.errors.map((e, i) => (
        <p key={i} className="text-muted-foreground flex items-start gap-1.5 text-xs">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {e.key ? <span className="font-mono">{e.key}</span> : null}
          {e.key ? ': ' : ''}
          {e.message}
        </p>
      ))}
      {result.items.map((item) => (
        <div
          key={item.key}
          className="grid gap-1.5 rounded-md border p-2 text-xs"
          data-testid="mcp-import-item"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-medium">{item.name}</span>
            <Badge variant="outline">{item.transport === 'stdio' ? 'stdio' : 'HTTP'}</Badge>
            <Badge
              variant={item.status === 'skip' ? 'destructive' : 'secondary'}
              className="ml-auto"
            >
              {item.status === 'skip'
                ? 'skipped'
                : item.status === 'create'
                  ? 'will be added'
                  : 'added'}
            </Badge>
          </div>
          {item.target && (
            <code className="bg-muted rounded px-1.5 py-1 break-all">
              {item.target}
              {item.args.length > 0 && ` ${item.args.join(' ')}`}
            </code>
          )}
          {item.reason && <p className="text-destructive">{item.reason}</p>}
          {item.variables.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.variables.map((v) => (
                <VariableBadge key={`${v.usedBy}:${v.key}`} v={v} />
              ))}
            </div>
          )}
          {item.warnings.map((w) => (
            <p key={w} className="text-muted-foreground">
              {w}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Paste an mcp.json (Claude Desktop, Claude Code, Cursor, VS Code), preview, then import. */
export function McpJsonImportDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [preview, setPreview] = useState<McpImportResult | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<McpImportResult>('/mcp-servers/import', { method: 'POST', body: { content, dryRun } }),
    onSuccess: (result) => {
      setPreview(result);
      if (result.dryRun) return;
      const added = result.items.filter((i) => i.status === 'created');
      const missing = added.flatMap((i) => i.variables.filter((v) => v.action === 'missing'));
      toast.success(`Added ${added.length} MCP server${added.length === 1 ? '' : 's'}`, {
        description: missing.length
          ? `Set ${[...new Set(missing.map((m) => m.key))].join(', ')} on the Environment page, then use Test.`
          : 'Use Test on each server to list its tools.',
      });
      void queryClient.invalidateQueries({ queryKey: ['mcp-servers', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['env-vars', tenantId] });
      if (added.length > 0) setOpen(false);
    },
  });
  const creatable = preview?.dryRun ? preview.items.filter((i) => i.status === 'create').length : 0;
  const missing = (preview?.items ?? []).flatMap((i) =>
    i.status === 'skip' ? [] : i.variables.filter((v) => v.action === 'missing'),
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          run.reset();
          setPreview(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <FileJson aria-hidden /> Import mcp.json
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import from mcp.json</DialogTitle>
          <DialogDescription>
            Paste the config from Claude Desktop, Claude Code (<code>.mcp.json</code>), Cursor,
            Windsurf or VS Code. Values in <code>env</code> and credential headers are saved as
            encrypted secrets; <code>{'${VAR}'}</code> placeholders read existing environment
            variables.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5">
          <Label htmlFor="mcp-json">mcp.json</Label>
          <Textarea
            id="mcp-json"
            className="min-h-48 font-mono text-xs"
            placeholder={EXAMPLE}
            value={content}
            spellCheck={false}
            onChange={(e) => {
              setContent(e.target.value);
              setPreview(null);
            }}
          />
          <Button
            variant="link"
            size="sm"
            className="h-auto justify-self-start p-0 text-xs"
            onClick={() => {
              setContent(EXAMPLE);
              setPreview(null);
            }}
          >
            Use the example
          </Button>
        </div>
        <ErrorAlert error={run.error} />
        {preview && <Plan result={preview} />}
        {missing.length > 0 && (
          <p className="text-muted-foreground text-xs">
            After importing, set {[...new Set(missing.map((m) => m.key))].join(', ')} on the{' '}
            <Link to="/environment" className="underline">
              Environment
            </Link>{' '}
            page.
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            disabled={!content.trim() || run.isPending}
            onClick={() => run.mutate(true)}
          >
            Preview
          </Button>
          <Button disabled={creatable === 0 || run.isPending} onClick={() => run.mutate(false)}>
            {run.isPending && run.variables === false
              ? 'Importing…'
              : `Import${creatable ? ` ${creatable} server${creatable === 1 ? '' : 's'}` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
